import { isValidElement, ReactNode, useEffect, useRef, useState } from 'react';
import {
  Box,
  BoxProps,
  Breadcrumb,
  BreadcrumbLink,
  BreadcrumbLinkProps,
  ButtonGroup,
  ButtonGroupProps,
  Text,
  TextProps,
  useResponsive,
} from '@bitrise/bitkit';
import ReactDOM from 'react-dom';
import { HEADER_HEIGHT, NAV_WIDTH } from '@/constants';

interface SubHeaderBreadcrumbProps {
  data: BreadcrumbLinkProps[] | undefined;
  isMobile?: boolean;
  isCompact?: boolean;
  pageTitle: string;
  rightContent?: ReactNode;
}

const SUBHEADER_VERTICAL_PADDING = 32;
export const SUBHEADER_STICKY_HEIGHT = 72;

export const SubHeaderBreadcrumb = ({
  data = [],
  isCompact,
  isMobile,
  pageTitle,
  rightContent,
}: SubHeaderBreadcrumbProps): JSX.Element | null => {
  if (data.length === 0) {
    return null;
  }

  const viewedData = isMobile ? data.slice(-1) : data;
  const items = viewedData.map((currentData) => {
    const { key, ...restProps } = currentData;
    return <BreadcrumbLink key={key} {...restProps} />;
  });

  if (isCompact && pageTitle) {
    items.push(
      <BreadcrumbLink key={pageTitle} isCurrentPage>
        {pageTitle}
      </BreadcrumbLink>,
    );
  }

  return (
    /*
     * minWidth is needed to text-overflow: ellipsis to work inside a flex layout
     * Check this article for explanation: https://css-tricks.com/flexbox-truncated-text/
     */
    <Box alignItems="center" display="flex" justifyContent="space-between" minWidth={0} overflow="hidden">
      <Breadcrumb hasSeparatorAfterLast={!isMobile && !isCompact} hasSeparatorBeforeFirst={isMobile} minWidth={0}>
        {items}
      </Breadcrumb>
      {!isCompact && rightContent}
    </Box>
  );
};

interface SubHeaderTitleTextProps extends TextProps {
  title: string;
  isSticky?: boolean;
}

export const SubHeaderTitleText = ({ title, isSticky = false, ...rest }: SubHeaderTitleTextProps): JSX.Element => {
  const { isMobile } = useResponsive();
  const asProp = isSticky || isMobile ? 'h3' : 'h1';

  return (
    <Text
      as={asProp}
      marginBottom={isSticky ? '0' : '4'}
      textStyle={['heading/h3', isSticky ? 'heading/h3' : 'heading/h1']}
      textColor="text/primary"
      wordBreak="break-word"
      {...rest}
    >
      {title}
    </Text>
  );
};

const SubHeaderButtonGroup = ({ children, ...rest }: ButtonGroupProps) => {
  if (!children) {
    return null;
  }

  return (
    <ButtonGroup
      display="flex"
      flexDirection={['column-reverse', 'row']}
      flexWrap={['wrap', 'nowrap']}
      marginLeft={['unset', 'unset', 'auto']}
      spacing={0}
      w={['100%', 'initial']}
      {...rest}
    >
      {children}
    </ButtonGroup>
  );
};

export interface SubHeaderProps {
  breadcrumbData?: BreadcrumbLinkProps[];
  children?: ReactNode;
  maxWidth?: BoxProps['maxWidth'];
  pageTitleContent?: ReactNode;
  pageTitleString?: string;
  rightContent?: ReactNode;
  onStickyChange?: (isSticky: boolean) => void;
  shouldSticky?: boolean;
}

const SubHeader = ({
  breadcrumbData,
  children,
  maxWidth,
  onStickyChange,
  pageTitleContent,
  pageTitleString,
  rightContent,
  shouldSticky,
}: SubHeaderProps): JSX.Element => {
  const { isMobile } = useResponsive();

  const header: HTMLElement | null = document.querySelector('.platform-header');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const subHeaderRef = useRef<HTMLDivElement | null>(null);

  const [subHeaderHeight, setSubHeaderHeight] = useState(0);

  const [isSticky, setSticky] = useState(false);

  useEffect(() => {
    if (!shouldSticky || isMobile) {
      return setSticky(false);
    }

    const ref = containerRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        setSticky(!entries[0].isIntersecting);
      },
      {
        rootMargin: `-${HEADER_HEIGHT + SUBHEADER_STICKY_HEIGHT}px`,
      },
    );

    if (ref) {
      observer.observe(ref);
    }

    return () => {
      observer.disconnect();
    };
  }, [isMobile, shouldSticky, isSticky]);

  useEffect(() => {
    if (onStickyChange) {
      onStickyChange(!!isSticky);
    }
  }, [isSticky, onStickyChange]);

  const pageTitleNode =
    isValidElement(pageTitleContent) || !pageTitleContent ? (
      pageTitleContent
    ) : (
      <SubHeaderTitleText title={pageTitleContent as string} isSticky={isSticky} />
    );

  const breadcrumbProps: Omit<SubHeaderBreadcrumbProps, 'isSticky'> = {
    data: breadcrumbData,
    isMobile,
    pageTitle: pageTitleString || (typeof pageTitleContent === 'string' ? pageTitleContent : ''),
    rightContent,
  };

  useEffect(() => {
    setSubHeaderHeight(containerRef?.current?.offsetHeight || 0);
  }, []);

  return (
    <Box ref={containerRef} minHeight={['initial', subHeaderHeight]} maxW={maxWidth}>
      {isSticky && header ? (
        ReactDOM.createPortal(
          <Box
            ref={subHeaderRef}
            alignItems="center"
            backgroundColor="neutral.100"
            boxShadow="0 2px 4px rgba(0, 0, 0, 0.06)"
            className="sticky-sub-header" // useScrollStepLog uses this class
            display="flex"
            justifyContent="space-between"
            padding="16"
            paddingLeft={NAV_WIDTH + 32}
            position="absolute"
            role="banner"
            width="100%"
          >
            <SubHeaderBreadcrumb {...breadcrumbProps} isCompact />
            <Box alignItems="center" display="flex" flex={1} gap={8} justifyContent="space-between">
              {!breadcrumbData && pageTitleNode}
              <SubHeaderButtonGroup gap={8}>{children}</SubHeaderButtonGroup>
            </Box>
          </Box>,
          header,
        )
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          gap={16}
          paddingBlockEnd={[16, SUBHEADER_VERTICAL_PADDING]}
          paddingBlockStart="24"
          role="banner"
        >
          <SubHeaderBreadcrumb {...breadcrumbProps} isCompact={false} />
          {(pageTitleNode || children) && (
            <Box
              alignItems={['flex-start', 'flex-end']}
              display="flex"
              flex={1}
              flexDirection={['column', 'row']}
              gap={[24, 16]}
              paddingX={[4, 0]}
              justifyContent="space-between"
            >
              {pageTitleNode}
              <SubHeaderButtonGroup gap={16} paddingInline={0} paddingBlockEnd={0}>
                {children}
              </SubHeaderButtonGroup>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
export default SubHeader;
